// src/components/PDF/MergePDF.js
import React, { useState } from 'react';
import { Upload, X, Download, FileText, Plus } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { pdfService } from '../../services/pdfService';
import { documentService } from '../../services/documentService';
import PDFPreviewSave from './PDFPreviewSave';
import { useAuthGuard } from '../../hooks/useAuthGuard';

const MergePDF = ({ isLoading, setIsLoading }) => {
  const { requireAuth, AuthModal } = useAuthGuard();
  const [files, setFiles] = useState([]);
  const [mergedFile, setMergedFile] = useState(null);
  const [showPreviewSave, setShowPreviewSave] = useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true,
    onDrop: (acceptedFiles) => {
      setFiles(prev => [...prev, ...acceptedFiles]);
    }
  });

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const mergePDFs = async () => {
    if (files.length < 2) {
      toast.error('Please select at least 2 PDF files to merge');
      return;
    }

    setIsLoading(true);
    try {
      const mergedBlob = await pdfService.mergePDFs(files);
      setMergedFile({
        blob: mergedBlob,
        name: 'merged.pdf'
      });
      toast.success('PDFs merged successfully!');
    } catch (error) {
      toast.error('Error merging PDFs');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadMerged = () => {
    requireAuth(() => {
      if (mergedFile) {
        const url = URL.createObjectURL(mergedFile.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = mergedFile.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    });
  };

  const resetForm = () => {
    setFiles([]);
    setMergedFile(null);
  };

  const handleSaveComplete = () => {
    // Optionally redirect to documents or refresh
    toast.success('You can find your merged PDF in your document library!');
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">Merge PDF Files</h2>
      
      {/* File Upload Zone */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive 
            ? 'border-[#232751] bg-[#232751]/5' 
            : 'border-gray-300 hover:border-gray-400'
          }
        `}
      >
        <input {...getInputProps()} />
        <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <p className="text-sm font-medium text-gray-600 mb-2">
          {isDragActive ? 'Drop PDF files here...' : 'Click to select or drag PDF files here'}
        </p>
        <p className="text-xs text-gray-500">
          Support for multiple PDF files
        </p>
      </div>

      {/* Selected Files */}
      {files.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-medium text-gray-700">Selected Files ({files.length})</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {files.map((file, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-red-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={mergePDFs}
          disabled={files.length < 2 || isLoading}
          className="flex-1 bg-[#232751] text-white py-3 px-6 rounded-lg font-medium hover:bg-[#1b1f43] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          <Plus className="w-5 h-5 mr-2" />
          Merge PDFs
        </button>
        
        {files.length > 0 && (
          <button
            onClick={resetForm}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Download Section */}
      {mergedFile && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FileText className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-medium text-green-800">Merge Complete!</p>
                <p className="text-sm text-green-600">Your merged PDF is ready for download</p>
              </div>
            </div>
            <button
              onClick={downloadMerged}
              className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </button>
          </div>
        </div>
      )}

      {AuthModal}

      {/* Preview/Save Modal */}
      <PDFPreviewSave
        isOpen={showPreviewSave}
        onClose={() => setShowPreviewSave(false)}
        pdfBlob={mergedFile?.blob}
        fileName={mergedFile?.name}
        operationType="merged"
        onSaveComplete={handleSaveComplete}
      />
    </div>
  );
};

export default MergePDF;
